import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useContext } from "react";
import NavigationLink from "./NavigationLink";
import CollapsibleSection from "./CollapsibleSection";
import { useAuthInfo } from "../../context/AuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import {
  faBuilding,
  faUser,
  faUsers,
  faGear,
  faFolder,
  faListCheck,
  faFlag,
  faHome,
  faChartColumn,
  faUserCog,
  faBuildingUser,
  faChevronLeft,
  faChevronRight,
  faUserCheck,
  faFileInvoice,
  faFileContract,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Logo from "../../assets/images/twlogo.svg";
import Logowm from "../../assets/images/twlogo-wmode.svg";

import LogoIcon from "../../assets/images/twicon.svg";

const Sidebar = (props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hideHeaders, setHideHeaders] = useState(false);
  const location = useLocation();
  const scrollIdleTimerRef = useRef(null);
  const { userInfo } = useAuthInfo();
  const { theme, toggleTheme } = useContext(ThemeContext);

  useEffect(
    () => () => {
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    },
    []
  );

  const handleSidebarTopScroll = (e) => {
    const el = e.currentTarget;
    el.classList.add("sidebar-top--scrolling");
    if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    scrollIdleTimerRef.current = setTimeout(() => {
      el.classList.remove("sidebar-top--scrolling");
      scrollIdleTimerRef.current = null;
    }, 850);
  };

  const toggleSidebar = () => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      !isCollapsed ? "80px" : "260px"
    );
    if (!isCollapsed) {
      setHideHeaders(true);
      setTimeout(() => {
        setIsCollapsed(true);
      }, 50);
    } else {
      setIsCollapsed(false);
      setTimeout(() => {
        setHideHeaders(false);
      }, 300);
    }
  };

  return (
    <div
      className={`sidebar-wrapper ${isCollapsed ? "collapsed" : ""}`}
      data-pathname={location.pathname}
      {...props}
    >
      <div className="sidebar-top" onScroll={handleSidebarTopScroll}>
        <Link className="mb-4 mt-1" to="/home">
          <img className="logoicon" src={LogoIcon} alt="logoIcon" />
          <img
            className="logo"
            src={theme === "light" ? Logowm : Logo}
            alt="logo"
          />
        </Link>

        <CollapsibleSection
          title="Quick Links"
          isCollapsed={hideHeaders}
          defaultExpanded={true}
          sectionKey="quick-links"
        >
          <NavigationLink
            to="/home"
            label="Home"
            icon={faHome}
            isCollapsed={isCollapsed}
            tooltip="Home"
            className="home-icon"
          />
          <NavigationLink
            to="/tasks?assignedto=me"
            label="My Tasks"
            icon={faUserCheck}
            isCollapsed={isCollapsed}
            tooltip="My Tasks"
            className="my-tasks-icon"
            isActive={(match, location) => {
              if (location.pathname !== "/tasks") return false;
              const searchParams = new URLSearchParams(location.search);
              return searchParams.get("assignedto") === "me";
            }}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title="Menu"
          isCollapsed={hideHeaders}
          defaultExpanded={true}
          sectionKey="menu"
        >
          <NavigationLink
            to="/projects"
            label="Projects"
            icon={faFolder}
            isCollapsed={isCollapsed}
            className="projects-icon"
            tooltip="Projects"
            isActive={(match, location) => {
              return location.pathname.startsWith("/project");
            }}
          />
          <NavigationLink
            to="/tasks"
            label="Tasks"
            icon={faListCheck}
            isCollapsed={isCollapsed}
            className="tasks-icon"
            tooltip="Tasks"
            isActive={(match, location) => {
              if (location.pathname !== "/tasks") return false;
              const searchParams = new URLSearchParams(location.search);
              const assignedTo = searchParams.get("assignedto");
              return assignedTo !== "me";
            }}
          />
          <NavigationLink
            to="/sprints"
            label="Sprints"
            icon={faFlag}
            className="sprints-icon"
            isCollapsed={isCollapsed}
            tooltip="Sprints"
          />
          {userInfo.role.name === "super-admin" && (
            <NavigationLink
              to="/reports"
              label="Reports"
              className="reports-icon"
              icon={faChartColumn}
              isCollapsed={isCollapsed}
              tooltip="Reports"
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="CRM"
          isCollapsed={hideHeaders}
          defaultExpanded={false}
          sectionKey="crm"
        >
          <NavigationLink
            to="/companies"
            label="Companies"
            icon={faBuilding}
            className="companies-icon"
            isCollapsed={isCollapsed}
            tooltip="Companies"
          />
          <NavigationLink
            to="/contacts"
            label="Contacts"
            icon={faUser}
            className="contacts-icon"
            isCollapsed={isCollapsed}
            tooltip="Contacts"
          />
        </CollapsibleSection>

        {userInfo.role.name !== "user" && (
          <CollapsibleSection
            title="Finance"
            isCollapsed={hideHeaders}
            defaultExpanded={false}
            sectionKey="finance"
          >
            <NavigationLink
              to="/invoices"
              label="Invoices"
              icon={faFileInvoice}
              className="invoices-icon"
              isCollapsed={isCollapsed}
              tooltip="Invoices"
            />
            <NavigationLink
              to="/contracts"
              label="Contracts"
              icon={faFileContract}
              className="contracts-icon"
              isCollapsed={isCollapsed}
              tooltip="Contracts"
            />
          </CollapsibleSection>
        )}
      </div>

      <div className="sidebar-bottom">
        {userInfo.role.name !== "user" && (
          <CollapsibleSection
            title="Settings"
            isCollapsed={hideHeaders}
            defaultExpanded={false}
            sectionKey="settings"
          >
            <NavigationLink
              to="/users"
              label="Users"
              icon={faUsers}
              className="users-icon"
              isCollapsed={isCollapsed}
              tooltip="Users"
            />
            <NavigationLink
              to="/organizations"
              label="Organizations"
              icon={faBuildingUser}
              className="organizations-icon"
              isCollapsed={isCollapsed}
              tooltip="Organizations"
            />
            <NavigationLink
              to="/settings"
              label="Settings"
              icon={faGear}
              className="settings-icon"
              isCollapsed={isCollapsed}
              tooltip="Settings"
            />
          </CollapsibleSection>
        )}
      </div>

      <div className="sidebar-toggle" onClick={toggleSidebar}>
        <FontAwesomeIcon
          icon={isCollapsed ? faChevronRight : faChevronLeft}
          className="toggle-icon"
        />
      </div>
    </div>
  );
};

export default Sidebar;
export { NavLink };
